import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Table,
  Button,
} from 'reactstrap';
import { useAlertPlus } from 'hooks';
import AlertModal from 'components/AlertModal';
import Attachments from 'components/Requests/Attachments';
import Required from 'components/Required';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { Request, RequestFiles } from 'types/Request';
import { getRequest } from 'features/requests/requestsAPI';
import { getVencimiento } from 'helpers/vencimientos';
import FakeInput from 'components/FakeInput';
import { insurersWithCoinsurance, insurersWithToken } from 'components/Requests/Sessions';

const insurersWithAuthorizationPerSession = [
  39, // COESBA
  46, // Union Personal
  55, // Medife
  57, // Swiss
  60, // OSAP
  71, // Segunda ART
  74, // Federada
  77, // OSPe
];

function Edit() {
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const [request, setRequest] = useState<Request>();

  const [files, setFiles] = useState<RequestFiles>({});
  const [infoVencimiento, setInfoVencimiento] = useState('');

  const [showAuthorization, setShowAuthorization] = useState(false);
  const [showCoinsurance, setShowCoinsurance] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    getRequest(parseInt(id))
      .then(response => {
        if (!response) {
          alert(
            'Se produjo un error al intentar recuperar la información de la presentación. ' +
            'Por favor, intente nuevamente más tarde',
          );
          return;
        }

        const { request, files: newFiles } = response;

        if (!request.id || !request.insurer_id) {
          alert('Se produjo un error al intentar recuperar la información de la presentación. ' +
              'Por favor, intente nuevamente más tarde', () => (window.location.href = '/solicitudes'),
          );
          console.error('EditCOMEI', `No se pudo recuperar información sobre la solicitud #${id}`);
          console.info(request);
          return;
        }

        setFiles(newFiles);
        setRequest(request);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Cálculo de días para el vencimiento
  useEffect(() => {
    if (!request?.sessions || ![1, 4].includes(request.status_id)) {
      setInfoVencimiento('');
      return;
    }

    const diffDays = getVencimiento(request.sessions);
    if (!diffDays) {
      setInfoVencimiento('');
      return;
    }

    if (diffDays > 60) {
      setInfoVencimiento('Solicitud vencida');
    } else {
      setInfoVencimiento(`Restan ${ 60 - diffDays } días para que se venza la solicitud`);
    }
  }, [
    request?.sessions.length,
    request?.status_id,
  ]);

  useEffect(() => {
    if (!request) {
      return;
    }

    setShowAuthorization(insurersWithAuthorizationPerSession.includes(request.insurer_id));
    setShowCoinsurance(insurersWithCoinsurance.includes(request.insurer_id));
    setShowToken(insurersWithToken.includes(request.insurer_id));
  }, [request?.insurer_id]);

  if (!request) {
    return <>Cargando...</>;
  }

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <AlertModal
            isOpen={showAlert}
            message={message}
            onClose={alertCallback}
          />
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">
                    Presentación #{id}
                    {
                      request?.insurer_shortname
                        ? ' - ' + request.insurer_shortname
                        : ' '
                    }
                    {
                      request?.impairment
                        ? ' (Discapacidad)'
                        : ' '
                    }
                    {
                      request?.extended_quota
                        ? ' (Cupo Extendido)'
                        : ' '
                    }
                    <br />
                    <span style={{ fontSize: '1rem' }}>{ infoVencimiento }</span>
                  </CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/solicitudes')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {isLoading ? null : (
                <Form>
                  <FormGroup>
                    <Row form>
                      <Col sm={6}>
                        <Label for="recipient_id">
                          Número de Afiliado <Required />
                        </Label>
                        <FakeInput value={request.recipient_id ?? ''} />
                      </Col>
                      {
                        request?.authorization_id
                          ? <Col sm={6}>
                              <Label for="authorization_id">
                                Número de Aprobación
                              </Label>
                              <FakeInput value={ request.authorization_id } />
                            </Col>
                          : null
                      }
                    </Row>
                  </FormGroup>
                  {
                    request.recipient_name
                      ?
                        <FormGroup>
                          <Row form>
                            <Col sm={6}>
                              <Label for="recipient_name">Nombre del Afiliado</Label>
                              <Input
                                id="recipient_name"
                                name="recipient_name"
                                defaultValue={request.recipient_name}
                                disabled={true}
                              />
                            </Col>
                          </Row>
                        </FormGroup>
                      : null
                  }
                  <FormGroup>
                    <Label for="prescription_date">
                      Fecha Prescripción Médica <Required />
                    </Label>
                    <FakeInput
                      value={ request.prescription_date ? moment(request.prescription_date).format('DD/MM/YYYY') : '' }
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label for="diagnosis">
                      Diagnóstico <Required />
                    </Label>
                    <Input
                      id="diagnosis"
                      name="diagnosis"
                      type="textarea"
                      defaultValue={request.diagnosis ?? ''}
                      disabled={true}
                    />
                  </FormGroup>
                  {
                    request?.insurer_id == 36
                      ? <>
                          <FormGroup style={{ paddingLeft: '1.5rem' }}>
                            <Input
                              id="impairment"
                              name="impairment"
                              type="checkbox"
                              checked={request.impairment || false}
                              disabled={true}
                            />
                            <Label
                              for="impairment"
                              style={{ marginBottom: 0, verticalAlign: 'bottom' }}
                            >
                              Es discapacidad
                            </Label>
                          </FormGroup>
                          <FormGroup style={{ paddingLeft: '1.5rem' }}>
                            <Input
                              id="rebilling"
                              name="rebilling"
                              type="checkbox"
                              checked={request.rebilling || false}
                              disabled={true}
                            />
                            <Label
                              for="rebilling"
                              style={{ marginBottom: 0, verticalAlign: 'bottom' }}
                            >
                              Es refacturación
                            </Label>
                          </FormGroup>
                          <FormGroup style={{ paddingLeft: '1.5rem' }}>
                            <Input
                              id="exceptional"
                              name="exceptional"
                              type="checkbox"
                              checked={request.exceptional || false}
                              disabled={true}
                            />
                            <Label
                              for="exceptional"
                              style={{ marginBottom: 0, verticalAlign: 'bottom' }}
                            >
                              Es excepcional
                            </Label>
                          </FormGroup>
                          <FormGroup
                            row
                            className={request.exceptional ? 'd-block' : 'd-none'}
                          >
                            <Col>
                              <Input
                                id="exceptional_text"
                                name="exceptional_text"
                                type="textarea"
                                value={request.exceptional_text ?? ''}
                                disabled={true}
                              />
                            </Col>
                          </FormGroup>
                        </>
                      : null
                  }
                  <FormGroup row>
                    <Col md="6">
                      <Label for="practice_id">
                        Tipo de práctica <Required />
                      </Label>
                      <FakeInput value={request.practice_text ?? ''} />
                    </Col>
                    {
                      request.additional_text
                        ? <Col md="6">
                            <Label for="addtional_id">
                              Práctica adicional <Required />
                            </Label>
                            <FakeInput value={request.additional_text} />
                          </Col>
                        : null
                    }
                  </FormGroup>
                  <FormGroup>
                    <Label>Fechas de sesiones</Label>
                    <Table
                      className='align-items-center table-flush mb-0'
                      style={{ fontSize: '.8rem' }}
                      responsive={ false }
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>FECHA SESIÓN</th>
                          { showAuthorization ? <th>AUTORIZACIÓN</th> : null }
                          { showCoinsurance ? <th>COSEGURO</th> : null }
                          { showToken ? <th>TOKEN</th> : null }
                        </tr>
                      </thead>
                      <tbody>
                        {
                          request && request.sessions && Array.isArray(request.sessions)
                            ? request.sessions
                              .sort((a, b) => a.date.valueOf() - b.date.valueOf())
                              .map(({ date, authorization_id: authorizationId, coinsurance, token }, i) => (
                                <tr key={ i }>
                                  <td>{ i + 1 }</td>
                                  <td>{ moment(date).format('DD/MM/YYYY') }</td>
                                  { showAuthorization ? <td>{ authorizationId ?? ' - ' }</td> : null }
                                  { showCoinsurance ? <td>$ { coinsurance ?? '0.00' }</td> : null }
                                  { showToken ? <td>{ token ?? '' }</td> : null }
                                </tr>
                              ))
                            : null
                        }
                      </tbody>
                    </Table>
                  </FormGroup>
                  {
                    files && request
                      ? <Attachments
                          files={files}
                          setFiles={setFiles}
                          disabled={isLoading}
                          editable={false}
                          setLoading={setLoading}
                          previouslyAuthorized={false}
                          formData={ request }
                        />
                      : null
                  }
                </Form>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Edit;
